import { useQuery } from "@tanstack/react-query";
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import { generatePath } from "react-router-dom";

import ContentWrapper from "~/components/ContentWrapper";
import { PROJECT_DETAILS_PATH_WITH_TAB_ID } from "~/constants/appRoutes.ts";
import type { Client } from "~/modules/client/api/client/clientTypes.ts";
import type { Company } from "~/modules/client/api/company/companyTypes.ts";
import { orderQueries } from "~/modules/project/api/order/orderQueries.ts";
import type { Project } from "~/modules/project/api/project/projectTypes.ts";
import { useProjectsProjectPhases } from "~/modules/project/api/projectPhase/projectPhaseQueries.ts";
import type { ProjectPhase } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import ProjectBudgetSection
	from "~/modules/project/components/ProjectDetailsView/components/ProjectOverviewContent/components/ProjectBudgetSection";
import ProjectDetailsSection
	from "~/modules/project/components/ProjectDetailsView/components/ProjectOverviewContent/components/ProjectDetailsSection";
import { minutesToWorkdays } from "~/modules/timeTracking/utils/timeTrackingUtils.ts";
import type { User } from "~/modules/user/api/user/userTypes.ts";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";

type Props = {
	allUsers: User[];
	client: Client;
	company: Company;
	phases: ProjectPhase[];
	projectData: Project;
	projectHasStaffings: boolean;
	showBudget: boolean;
};
const ProjectOverviewContent: FunctionComponent<Props> = ({
	allUsers,
	client,
	company,
	projectData,
	projectHasStaffings,
	showBudget,
}) => {
	const {
		budgetCentsPlanned,
		budgetCentsTracked,
		clientId,
		endDate,
		managedBy,
		manDaysPlanned,
		minutesTracked,
		startDate,
	} = projectData;

	const {
		data: ordersData,
	} = useQuery(orderQueries.byProject(projectData.id));

	const {
		data: phasesData,
	} = useProjectsProjectPhases(projectData.id);

	const manDaysTracked = minutesToWorkdays(minutesTracked);
	const manDaysPlannedInt = parseInt(manDaysPlanned);

	const totalProjectBudgetCents = budgetCentsPlanned;

	const totalProjectManDaysBudget = manDaysPlannedInt;

	const projectLead = allUsers.find((u) => managedBy === u.id)?.fullName || "n/a";
	const clientDisplayName = client.displayName;
	const companyDisplayName = company.displayName;

	const overbookedOrders = useMemo(() => {
		if (!ordersData || !phasesData) return [];

		return ordersData
			.filter(order => order.budgetCents < order.budgetCentsTracked)
			.map(order => {
				const phase = phasesData.find(phase => phase.id === order.projectPhaseId)!;
				const percentageOverbooked = Math.round((order.budgetCentsTracked / order.budgetCents) * 100);
				const percentageOverbookedFormatted = percentageOverbooked !== Infinity
					? formatNumberWithComma(percentageOverbooked - 100, 2, true) + "%"
					: "";
				const centsOverbooked = order.budgetCentsTracked - order.budgetCents;
				const linkPath = generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, {
					projectId: projectData.id,
					tabId: "phase-" + phase.phaseNumber,
				});

				return {
					orderId: order.id,
					linkPath,
					phaseNumber: phase.phaseNumber,
					orderTitle: order.title,
					centsOverbooked,
					percentageOverbookedFormatted,
				};
			});
	}, [ordersData, phasesData, projectData.id]);


	return <ContentWrapper className="w-full overflow-y-auto pb-10 pt-4">
		<div className="grid grid-cols-2 gap-4">
			<div className="flex flex-col gap-y-4">
				<ProjectDetailsSection companyDisplayName={companyDisplayName}
									   clientDisplayName={clientDisplayName}
									   clientId={clientId}
									   endDate={endDate}
									   projectData={projectData}
									   projectHasStaffings={projectHasStaffings}
									   projectLead={projectLead}
									   startDate={startDate} />
			</div>
			<div className="flex flex-col gap-y-4">
				<ProjectBudgetSection budgetCentsPlanned={budgetCentsPlanned}
									  budgetCentsTracked={budgetCentsTracked}
									  manDaysPlanned={manDaysPlannedInt}
									  manDaysTracked={manDaysTracked}
									  totalBudgetCents={totalProjectBudgetCents}
									  totalManDays={totalProjectManDaysBudget}
									  showBudget={showBudget}
									  overbookedOrders={overbookedOrders} />
			</div>
		</div>

	</ContentWrapper>;
};

export default ProjectOverviewContent;